import { SortDirection } from "gql/generated/types";
import useSafeId from "hooks/dashboard/useDashboardComponentId";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTableValue } from "primereact/datatable";
import { Paginator } from "primereact/paginator";
import { Row } from "primereact/row";
import React, { WheelEvent, useCallback, useEffect, useRef, useState } from "react";
import { T } from "services/lang/LanguageService";
import { CellSubType, CellType, ColumnAggregationType, TableRowSize } from "utils/enums";
import DataCell from "./components/templates/table-components/data-cell/DataCell";
import AttributeHeader from "./components/templates/table-components/header/AttributeHeader";
import {
  IColumnsWithValuesProps,
  IProductTemplateAttribute,
  ITableDataEntry,
  ProductTemplateNonTransposedTableProps,
} from "./utils/interfaces";

import "./ProductTemplateTable.scss";
import {
  checkForHighlightType,
  formatDataTotalsAggregation,
  getHighlightColor,
  getValueColors,
  handlePrecision,
  sortTableData,
  styleHandler,
} from "./utils/utils";

export default function ProductTemplateTableDefault({
  templateData,
  setTemplateData,
  configData,
  selectedEntryBody,
  selectedEntryId,
  selectedEntryIndex,
  itemsPerPage,
  editedDataHandler,
  onClickSign,
  handleTableKeyDown,
  EditRowDataCell,
  reportOptions,
  editColumnLocation = "BACK",
  page,
  setPage,
  dynamicHeight,
  allowRowEdit,
  foreignKeyWrap,
  showTotals,
  hidePagination,
  isColumnsSortable,
  defaultSortingAttribute,
  defaultSorting,
  maxAge,
  updateFrequency,
  rowSize,
  textAlign,
  fontSize,
}: ProductTemplateNonTransposedTableProps) {
  const tableId = useSafeId();
  const divRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<DataTableValue | null>(null);
  const [rows, setRows] = useState(itemsPerPage);
  const [aggregatedColumns, setAggregatedColumns] = useState<IColumnsWithValuesProps[]>([]);
  const [cellColors, setCellColors] = useState<
    Record<string, { color?: string; bgColor?: string; colorEntireRow?: boolean }>
  >({});

  const [sortField, setSortField] = useState<string | null>(defaultSortingAttribute ?? null);
  const [sortOrder, setSortOrder] = useState<SortDirection>(defaultSorting ?? SortDirection.Asc);
  const rowsPerPageOptions = itemsPerPage
    ? [itemsPerPage, 10, 20, 30].sort((a, b) => a - b).filter((x, i, a) => a.indexOf(x) === i)
    : [];

  useEffect(() => {
    if (sortField && sortOrder) {
      const sorted = sortTableData(templateData, sortField, sortOrder);
      setTemplateData(sorted);
    }
  }, []);

  const handleWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
    if (event.altKey) {
      const div: HTMLDivElement = event.currentTarget as HTMLDivElement;
      div.scrollLeft += event.deltaY * 0.45;
    }
  }, []);

  const handleSort = (field: string) => {
    let newOrder = SortDirection.Asc;
    if (sortField === field) {
      if (sortOrder === SortDirection.Asc) {
        newOrder = SortDirection.Desc;
      } else {
        newOrder = SortDirection.Asc;
      }
    }

    setSortField(field);
    setSortOrder(newOrder);
    const sorted = sortTableData(templateData, field, newOrder);
    setTemplateData(sorted);
  };

  const generateFooterColumns = (aggregatedCols: IColumnsWithValuesProps[]) => {
    return aggregatedCols.map(col => {
      let tooltipText = "";
      let footerText: string | number = "";
      if (col.aggregationType === ColumnAggregationType.SUM) {
        tooltipText = T("word.sum");
        footerText = `${handlePrecision(col.total!, col.decimalPlaces!, true)} ${col.unit ?? ""}`;
      } else if (col.aggregationType === ColumnAggregationType.MEAN) {
        tooltipText = T("word.average");
        footerText = `${handlePrecision(col.total!, col.decimalPlaces!, true)} ${col.unit ?? ""}`;
      } else if (col.aggregationType === ColumnAggregationType.COUNT) {
        tooltipText = T("action.count");
        footerText = handlePrecision(col.total!, col.decimalPlaces!, true);
      }
      return (
        <Column
          key={col.referenceName}
          footer={
            <div title={tooltipText} className="text-center">
              {footerText}
            </div>
          }
        />
      );
    });
  };

  let footerGroup = null;
  if (showTotals && aggregatedColumns.length > 0) {
    footerGroup = (
      <ColumnGroup>
        <Row>{generateFooterColumns(aggregatedColumns)}</Row>
      </ColumnGroup>
    );
  }

  useEffect(() => {
    if (reportOptions && document.getElementsByClassName("print-mode-enabled").length > 0) {
      const table = document.querySelector(`#${tableId}`);
      if (table) {
        const allChildren = table.querySelectorAll("*");

        if (reportOptions.fontSize) {
          for (let i = 0; i < allChildren.length; i++) {
            allChildren[i].style.fontSize = reportOptions?.fontSize;
          }
        }
      }
    }
  }, [footerGroup]);

  useEffect(() => {
    setAggregatedColumns(formatDataTotalsAggregation(configData, templateData));
  }, [configData?.data?.attributes, templateData]);

  const editFront = editColumnLocation === "FRONT";
  const editBack = editColumnLocation === "BACK";
  const columns = configData?.data?.attributes ?? [];
  const currentPageStart = page * (rows ?? 0);

  const visibleRows = rows
    ? [...templateData.slice(currentPageStart, currentPageStart + rows)]
    : templateData;

  const renderFooterCells = () => {
    if (!showTotals || aggregatedColumns.length === 0) return null;

    return (
      <tfoot>
        <tr>
          {allowRowEdit && editFront && <td className="non-printable" />}
          {aggregatedColumns.map(col => {
            let tooltipText = "";
            let footerText: string | number = "";

            if (col.aggregationType === ColumnAggregationType.SUM) {
              tooltipText = T("word.sum");
              footerText = `${handlePrecision(col.total!, col.decimalPlaces!, true)} ${col.unit ?? ""}`;
            } else if (col.aggregationType === ColumnAggregationType.MEAN) {
              tooltipText = T("word.average");
              footerText = `${handlePrecision(col.total!, col.decimalPlaces!, true)} ${col.unit ?? ""}`;
            } else if (col.aggregationType === ColumnAggregationType.COUNT) {
              tooltipText = T("action.count");
              footerText = handlePrecision(col.total!, col.decimalPlaces!, true);
            }

            return (
              <td key={col.referenceName}>
                <div title={tooltipText} className={`font-bold text-${textAlign ?? "center"}`}>
                  {footerText}
                </div>
              </td>
            );
          })}
          {allowRowEdit && editBack && <td className="non-printable" />}
        </tr>
      </tfoot>
    );
  };

  const renderEditCell = (rowData: ITableDataEntry, rowIndex: number) => (
    <td className="non-printable" style={{ width: 0 }}>
      {EditRowDataCell(rowData, rowIndex)}
    </td>
  );

  const sortIconFor = (referenceName: string) => {
    const isSorted = sortField === referenceName;
    if (!isColumnsSortable) return null;

    let icon = "alt";
    if (isSorted) {
      icon = sortOrder === SortDirection.Asc ? "amount-up-alt" : "amount-down";
    } else {
      icon = "alt";
    }

    return <i className={`pi pi-sort-${icon}`} />;
  };

  const getCellPadding = () => {
    switch (rowSize) {
      case TableRowSize.MEDIUM:
        return 1;
      case TableRowSize.LARGE:
        return 2;
      default:
        return 0;
    }
  };

  const dataCellTemplate = (
    row: ITableDataEntry,
    absoluteIdx: number,
    col: IProductTemplateAttribute,
    i: number
  ) => {
    const dataValue = selectedEntryBody && selectedEntryIndex === absoluteIdx ? selectedEntryBody : row;
    const obj = dataValue.entries[col.referenceName];
    const key = `${row.id}-${col.referenceName}`;

    let color;
    let bgColor;
    if (obj && obj.type && checkForHighlightType(obj.type)) {
      if (obj && obj.type && checkForHighlightType(obj.type)) {
        [color, bgColor] = getValueColors(obj.value as number, {
          highlightRanges: col.bgHighlightRanges,
          defaultHighlightColor: col.highlightColor,
        });
      } else {
        [color, bgColor] = getHighlightColor(col.highlightColor);
      }
    } else {
      color = cellColors[key]?.color || "";
      bgColor = cellColors[key]?.bgColor || "";
    }

    const onHighlightColors = (c?: string, bg?: string, colorEntireRow?: boolean) => {
      setCellColors(prev => ({ ...prev, [key]: { color: c, bgColor: bg, colorEntireRow: colorEntireRow } }));
    };

    const cellStyle = cellColors[key]?.colorEntireRow
      ? undefined
      : styleHandler(color, bgColor, obj && obj.type);

    return (
      <td key={`${row.id}-${col.referenceName}`} style={cellStyle}>
        <div className={`text-${getTextAlignment(false, col)} h-full p-${getCellPadding()}`}>
          <DataCell
            headerConfig={col}
            dataValue={dataValue}
            indexHeader={i}
            indexEntry={absoluteIdx}
            changeHandler={editedDataHandler}
            isInEditMode={selectedEntryId === row.id}
            onClickSign={onClickSign}
            foreignKeyWrap={foreignKeyWrap}
            editRequireRoles={col.editRequireRoles ?? []}
            maxAge={maxAge}
            updateFrequency={updateFrequency}
            onHighlightColors={onHighlightColors}
          />
        </div>
      </td>
    );
  };

  const getRowStyle = (row: ITableDataEntry) => {
    const rowCellColors = Object.entries(cellColors)
      .filter(([key]) => key.startsWith(`${row.id}-`))
      .map(([, value]) => value);
    const rowHighlight = rowCellColors.find(c => c.colorEntireRow && (c.color || c.bgColor));

    return rowHighlight ? styleHandler(rowHighlight.color, rowHighlight.bgColor, "row") : undefined;
  };

  const getTextAlignment = (isHeader: boolean, column: IProductTemplateAttribute) => {
    if (textAlign) return textAlign;
    if (column.type === CellType.FILE) return "center";
    if (
      column.type === CellType.STRING ||
      (column.type === CellType.MULTI_VALUE && column.subType === CellSubType.STRING) ||
      column.type === CellType.DATETIME ||
      column.type === CellType.INSTANT
    ) {
      return isHeader ? "start" : "left";
    }
    return isHeader ? "end" : "right";
  };

  const handleSelectRow = (row: ITableDataEntry) => {
    if (row.id === selectedEntryId) return null;
    if (row === selected) return setSelected(null);
    return setSelected(row);
  };

  return (
    <div className="flex flex-column justify-content-between h-full">
      <div
        className="default-table"
        id={tableId}
        style={{
          overflowX: "auto",
          maxHeight: dynamicHeight ? "auto" : "700px",
          ["--table-font-size" as string]: `${fontSize}pt`,
        }}
        onKeyDown={e => handleTableKeyDown(e.key)}
        ref={divRef}
        onWheel={handleWheel}
      >
        <table className="p-datatable p-component">
          <thead>
            <tr className="table-header">
              {allowRowEdit && editFront && <th className="non-printable" />}
              {columns.map(col => (
                <th
                  key={col.referenceName}
                  onClick={() => isColumnsSortable && handleSort(col.referenceName)}
                  className="table-header-cell"
                  style={{ textAlign: "center", cursor: isColumnsSortable ? "pointer" : "default" }}
                >
                  <div
                    className={`flex align-items-center gap-2 justify-content-${getTextAlignment(true, col)}`}
                  >
                    <AttributeHeader key={col.referenceName} attribute={col} noTh />
                    {sortIconFor(col.referenceName)}
                  </div>
                </th>
              ))}
              {allowRowEdit && editBack && <th className="non-printable" />}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, localIdx) => {
              const absoluteIdx = currentPageStart + localIdx - page;
              const isSelected = selected?.id === row.id;
              return (
                <tr
                  key={row.id}
                  className={isSelected ? "is-selected" : ""}
                  onClick={() => handleSelectRow(row)}
                  style={getRowStyle(row)}
                >
                  {allowRowEdit && editFront && renderEditCell(row, absoluteIdx)}

                  {columns.map((col, i) => dataCellTemplate(row, absoluteIdx, col, i))}

                  {allowRowEdit && editBack && renderEditCell(row, absoluteIdx)}
                </tr>
              );
            })}
          </tbody>

          {renderFooterCells()}
        </table>
      </div>
      {!!itemsPerPage && !hidePagination && (
        <Paginator
          totalRecords={templateData.length}
          rows={rows}
          first={page * (rows ?? 0)}
          onPageChange={e => {
            setPage(e.page);
            setRows(e.rows);
          }}
          template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate={`${T("word.showing")} {first} ${T("word.to")} {last} ${T("word.of")} {totalRecords}`}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
    </div>
  );
}
